import { EVENTS, daysBucket, durationBucket, timeBucket, track, turnsBucket } from './analytics';

/**
 * The customer journey on top of `track`: the call sites say what happened (an app was asked for, a preview is ready, a turn
 * ended, the project was opened or left) and this module decides which business event that is, with which buckets.
 *
 *  - A project is never named. What has to be remembered about it in this browser (the day it was last opened, whether it ever
 *    had a preview) sits in localStorage under an opaque hash of its id, and only buckets derived from it are sent.
 *  - "The app just asked for" lives in sessionStorage, so it survives the Home → Chat → Code navigation and a reload of the tab.
 *  - A working session on a project ends once: when the user leaves the project view, closes the tab (pagehide), or after
 *    30 minutes without activity. Switching to another tab does not end it; coming back after more than 30 minutes away ends
 *    the old one there and starts a new one. Activity after an end starts a new one too.
 */

const JOURNEY_KEY = 'flow.analytics.journey';
const PROJECT_PREFIX = 'flow.analytics.p.';
const IDLE_MS = 30 * 60_000;
const LEAVE_GRACE_MS = 2000; // a remount of the project view is not leaving it
const PREVIEW_WAIT_MS = 45_000; // how long a change waits for the preview that follows it
const JOURNEY_TTL_MS = 2 * 60 * 60_000;
const MAX_RETRIES = 10;
const DAY_MS = 86_400_000;

const store = (kind) => ({
  get: (key) => { try { return window[kind].getItem(key); } catch (_error) { return null; } },
  set: (key, value) => { try { window[kind].setItem(key, value); } catch (_error) { /* not remembered */ } },
  remove: (key) => { try { window[kind].removeItem(key); } catch (_error) { /* nothing to forget */ } },
});
const local = store('localStorage');
const tab = store('sessionStorage');
const readJson = (storage, key) => { try { return JSON.parse(storage.get(key) || 'null'); } catch (_error) { return null; } };

/** An opaque, stable key for a project id (cyrb53): the id itself is never stored under analytics keys nor sent. */
export const opaqueKey = (id) => {
  const text = `flow:${id}`;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

const startOfDay = (time) => { const date = new Date(time); return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(); };
const daysBetween = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);
/** The stack as a short slug ("React + Vite" → "react-vite"), or 'unknown'. */
const stackSlug = (stack) => String(stack || '').toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'unknown';
const OUTCOME = {ok: 'preview_ok', broken: 'preview_broken'};
const RESULT = {ok: 'working_preview', broken: 'broken_preview'};

const projects = new Map(); // opaque key -> {openedAt, createdAt, preview: 'ok' | 'broken' | undefined, failures, lastPreviewAt, lastTurnEndAt, pending}
let session = null; // {key, startedAt, turns, published, ended, idleTimer, leaveTimer}
let viewKey = null; // the project the project view is open on, if any
let hiddenAt = null;
let lifecycleInstalled = false;

const projectState = (key) => {
  if (!projects.has(key)) { projects.set(key, {failures: 0}); }
  return projects.get(key);
};
const readRecord = (key) => readJson(local, `${PROJECT_PREFIX}${key}`) || {};
const writeRecord = (key, record) => local.set(`${PROJECT_PREFIX}${key}`, JSON.stringify(record));
const readJourney = () => {
  const journey = readJson(tab, JOURNEY_KEY);
  return journey && Date.now() - journey.at < JOURNEY_TTL_MS ? journey : null;
};
const writeJourney = (journey) => tab.set(JOURNEY_KEY, JSON.stringify(journey));

const emitChange = (turnNumber, preview) => {
  track(EVENTS.CHANGE_APPLIED, {turn_number_bucket: turnsBucket(turnNumber), outcome: OUTCOME[preview] || 'no_preview'});
};
const flushPending = (key) => {
  const state = projects.get(key);
  if (!state?.pending) { return; }
  clearTimeout(state.pending.timer);
  const {turnNumber} = state.pending;
  state.pending = null;
  emitChange(turnNumber, state.preview);
};

/** The working session is over: report how it went, once. */
export const endSession = () => {
  if (!session || session.ended) { return; }
  session.ended = true;
  clearTimeout(session.idleTimer);
  clearTimeout(session.leaveTimer);
  flushPending(session.key);
  const state = projects.get(session.key);
  track(EVENTS.SESSION_OUTCOME, {
    result: session.published ? 'published' : RESULT[state?.preview] || 'no_preview',
    turns_bucket: turnsBucket(session.turns),
    duration_bucket: durationBucket(Date.now() - session.startedAt),
  });
};

const installLifecycle = () => {
  if (lifecycleInstalled || typeof window === 'undefined') { return; }
  lifecycleInstalled = true;
  window.addEventListener('pagehide', endSession);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    const awayLong = hiddenAt !== null && Date.now() - hiddenAt > IDLE_MS;
    hiddenAt = null;
    if (!awayLong) { return; }
    endSession();
    if (viewKey) { ensureSession(viewKey); }
  });
};

const touch = () => {
  clearTimeout(session.idleTimer);
  session.idleTimer = setTimeout(endSession, IDLE_MS);
};

/** The session for this project, started (or restarted after it ended) if needed. */
const ensureSession = (key) => {
  if (session && !session.ended && session.key === key) {
    clearTimeout(session.leaveTimer);
    touch();
    return session;
  }
  endSession();
  installLifecycle();
  session = {key, startedAt: Date.now(), turns: 0, published: false, ended: false};
  touch();
  return session;
};

/** The first prompt of a new app, from Home. */
export const appRequested = ({mode, playbook, hasFiles = false, viaVoice = false} = {}) => {
  writeJourney({at: Date.now(), turns: 0, failures: 0});
  track(EVENTS.APP_REQUESTED, {mode: mode || 'full', playbook: playbook || 'none', has_files: Boolean(hasFiles), via_voice: Boolean(viaVoice)});
};

/** The project view opened a project: it may be a return on a later day, and a working session starts. */
export const projectOpened = (projectId, {createdAt} = {}) => {
  if (!projectId) { return; }
  const key = opaqueKey(projectId);
  const state = projectState(key);
  const created = createdAt ? new Date(createdAt).getTime() : NaN;
  if (Number.isFinite(created)) { state.createdAt = created; }
  const now = Date.now();
  const record = readRecord(key);
  if (record.day && startOfDay(now) > record.day) {
    track(EVENTS.PROJECT_RETURN, {days_since_bucket: daysBucket(daysBetween(record.day, now))});
  }
  if (record.day !== startOfDay(now)) { writeRecord(key, {...record, day: startOfDay(now)}); }
  if (!(session && !session.ended && session.key === key)) { state.openedAt = now; }
  viewKey = key;
  ensureSession(key);
};

/** The project view went away: the session ends unless the same project comes right back (a remount). */
export const projectLeft = (projectId) => {
  if (!projectId || !session || session.ended || session.key !== opaqueKey(projectId)) { return; }
  clearTimeout(session.leaveTimer);
  session.leaveTimer = setTimeout(() => { viewKey = null; endSession(); }, LEAVE_GRACE_MS);
};

/** A preview finished building (`ok`) or failed to. The first good one of a project in this browser is `first_preview`. */
export const previewResult = (projectId, ok, {stack} = {}) => {
  if (!projectId) { return; }
  const key = opaqueKey(projectId);
  const state = projectState(key);
  const current = ensureSession(key);
  const now = Date.now();
  state.preview = ok ? 'ok' : 'broken';
  state.lastPreviewAt = now;
  const journey = readJourney();
  if (!ok) {
    state.failures += 1;
    if (journey) { writeJourney({...journey, failures: journey.failures + 1}); }
    flushPending(key);
    return;
  }
  flushPending(key);
  const record = readRecord(key);
  if (record.fp) { return; }
  writeRecord(key, {...record, fp: 1});
  const since = journey?.at || state.openedAt || current.startedAt;
  track(EVENTS.FIRST_PREVIEW, {
    stack: stackSlug(stack),
    time_bucket: timeBucket(now - since),
    turns_bucket: turnsBucket(journey ? journey.turns : current.turns),
    build_retries: Math.min(journey ? journey.failures : state.failures, MAX_RETRIES),
  });
  if (journey) { tab.remove(JOURNEY_KEY); }
};

/**
 * An agent turn ended. If it changed the app (`changed`), `change_applied` reports whether the preview works afterwards: at
 * once when a preview result already came in during the turn, otherwise with the next one (or what is known after 45 s).
 */
export const turnCompleted = (projectId, {changed = true} = {}) => {
  // Every turn is someone working with the AI (it makes them active in Flow admin › Analytics), changed or not.
  track(EVENTS.CHAT_TURN, {changed: Boolean(changed)});
  const journey = readJourney();
  if (journey) { writeJourney({...journey, turns: journey.turns + 1}); }
  if (!projectId) {
    if (changed) { emitChange(journey ? journey.turns + 1 : 1, undefined); }
    return;
  }
  const key = opaqueKey(projectId);
  const state = projectState(key);
  const current = ensureSession(key);
  current.turns += 1;
  const previousTurnEnd = state.lastTurnEndAt || current.startedAt;
  state.lastTurnEndAt = Date.now();
  if (!changed) { return; }
  if (state.lastPreviewAt && state.lastPreviewAt > previousTurnEnd) {
    emitChange(current.turns, state.preview);
    return;
  }
  flushPending(key);
  state.pending = {turnNumber: current.turns, timer: setTimeout(() => flushPending(key), PREVIEW_WAIT_MS)};
};

/** A rollback: from the history, to the previous version ('last') or further back ('older'); by the agent, 'unknown'. */
const HOW_FAR = new Set(['last', 'older', 'unknown']);
export const undone = (howFar) => track(EVENTS.UNDO, {how_far: HOW_FAR.has(howFar) ? howFar : 'older'});

export const published = (projectId, target) => {
  const params = {target};
  if (projectId) {
    const key = opaqueKey(projectId);
    const state = projectState(key);
    ensureSession(key).published = true;
    if (state.createdAt) { params.days_since_create_bucket = daysBucket(daysBetween(state.createdAt, Date.now())); }
  }
  track(EVENTS.PUBLISH, params);
};

/** Something got in the way: an error shown, a publish that failed, a prompt typed and left. `area` is a fixed string. */
export const friction = (area, recoverable = true) => track(EVENTS.FRICTION, {area, recoverable: Boolean(recoverable)});

/** For tests only: forget the session and what is known about projects in memory. */
export const resetJourneyForTests = () => {
  if (session) { clearTimeout(session.idleTimer); clearTimeout(session.leaveTimer); }
  projects.forEach((state) => state.pending && clearTimeout(state.pending.timer));
  projects.clear();
  session = null;
  viewKey = null;
  hiddenAt = null;
};
